/* eslint-disable react/require-default-props */
import React, { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { fontString } from '@/constants/constants';

export type TypographyVariant =
  | 'display1'
  | 'display2'
  | 'display3'
  | 'caption'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'h7'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'button1'
  | 'button2';

type TypographyHtmlTags = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

/** Maps the TypographyVariant to HTML elements */
export const variantsMapping: Record<TypographyVariant, TypographyHtmlTags> = {
  body1: 'p',
  body2: 'p',
  body3: 'p',
  button1: 'p',
  button2: 'p',
  caption: 'p',
  display1: 'p',
  display2: 'p',
  display3: 'p',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  h7: 'h6',
};

/** Tailwind classnames for each variant */
export const tailwindTypographyClassNameMapping: Record<
  TypographyVariant,
  string
> = {
  body1: 'text-[16px] leading-[24px] font-normal tracking-[0.3px]',
  body2: 'text-[14px] leading-[20px] font-normal tracking-[0.3px]',
  body3: 'text-[12px] leading-[20px] font-normal tracking-[0.3px]',
  button1: 'text-[16px] leading-[22.4px] font-medium tracking-[0.3px]',
  button2: 'text-[14px] leading-[19.6px] font-medium tracking-[0.3px]',
  caption: 'text-[12px] leading-[16.8px] font-normal tracking-[0.3px]',
  display1: 'text-[78px] leading-[78px] font-light tracking-[0.3px]',
  display2: 'text-[60px] leading-[60px] font-light tracking-[0.3px]',
  display3: 'text-[48px] leading-[48px] font-light tracking-[0.3px]',
  h1: 'text-[40px] leading-[48px] font-medium tracking-[0.3px]',
  h2: 'text-[32px] leading-[40px] font-medium tracking-[0.3px]',
  h3: 'text-[24px] leading-[28px] font-medium tracking-[0.3px]',
  h4: 'text-[20px] leading-[24px] font-medium tracking-[0.3px]',
  h5: 'text-[16px] leading-[20px] font-medium tracking-[0.3px]',
  h6: 'text-[14px] leading-[18px] font-medium tracking-[0.3px]',
  h7: 'text-[12px] leading-[18px] font-medium tracking-[0.3px]',
};

export enum TypographyFont {
  Roboto,
  Monsterrat,
}

const TypographyFontMap: Record<TypographyFont, string> = {
  [TypographyFont.Roboto]: fontString,
  [TypographyFont.Monsterrat]: 'Montserrat',
};

type Props = React.HTMLAttributes<HTMLElement> & {
  variant?: TypographyVariant;
  children?: ReactNode;
  className?: string;
  font?: TypographyFont;
};

/**
 * TypographyV2 is for the [Unified Design system](https://www.figma.com/file/Vd7dvy5BLNcY5pDkQ2j0dj/Unified-Design-System-(Working)?node-id=0-1&t=goyzIDjmMxPaB0uA-0)
 *   - For the old Acres Design system (don't), use Typography found in /components/common/typography
 *   - For the Unified Design System, use this Typography
 */
const Typography: React.FunctionComponent<Props> = React.forwardRef<
  HTMLParagraphElement | HTMLHeadingElement,
  Props
>(
  (
    {
      children = null,
      className = undefined,
      variant = 'body1',
      font = TypographyFont.Roboto,
      ...other
    }: Props,
    ref
  ) => {
    const Component = React.createElement(
      typeof children === 'string' ? variantsMapping[variant!] : 'div',
      {
        className: twMerge(
          `${tailwindTypographyClassNameMapping[variant!]}`,
          className
        ),
        ref,
        style: { fontFamily: TypographyFontMap[font] },
        ...other,
      },
      children
    );

    return Component;
  }
);

export default Typography;
